/**
 * Keyboard Shortcuts Hook
 * 
 * WHY: The UI/UX Expert mandated that the Vetting interface must be
 * navigable 100% via keyboard for power users processing hundreds of
 * findings. "Mouse-clicking through dropdowns is too slow."
 * 
 * This hook provides:
 * - j/k navigation (vim-style, familiar to technical users)
 * - v for vet (confirm), f for false positive, m for mitigated, d for defer
 * - Enter to expand/collapse current item
 * - Escape to clear selection
 * 
 * @see .docs/architecture/software_iq_architecture_review.md - Expert 2
 */

import { useEffect, useCallback, useRef } from 'react'

/**
 * Keyboard shortcut definition.
 */
interface KeyboardShortcut {
  /** Key to trigger the action (e.g., 'j', 'k', 'Enter') */
  key: string
  /** Callback when key is pressed */
  action: () => void
  /** Optional modifier keys */
  modifiers?: {
    ctrl?: boolean
    shift?: boolean
    alt?: boolean
    meta?: boolean
  }
  /** Description for help dialog */
  description?: string
}

/**
 * Options for the keyboard shortcuts hook.
 */
interface UseKeyboardShortcutsOptions {
  /** Whether shortcuts are enabled */
  enabled?: boolean
  /** Prevent default browser behavior */
  preventDefault?: boolean
  /** Stop event propagation */
  stopPropagation?: boolean
}

/**
 * Hook to register keyboard shortcuts.
 * 
 * @example
 * useKeyboardShortcuts([
 *   { key: 'j', action: () => selectNext(), description: 'Next item' },
 *   { key: 'k', action: () => selectPrev(), description: 'Previous item' },
 *   { key: 'v', action: () => vetCurrent(), description: 'Confirm finding' },
 * ])
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  options: UseKeyboardShortcutsOptions = {}
) {
  const { enabled = true, preventDefault = true, stopPropagation = false } = options

  // Use ref to avoid stale closures
  const shortcutsRef = useRef(shortcuts)
  shortcutsRef.current = shortcuts

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip if disabled
      if (!enabled) return

      // Skip if user is typing in an input
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return
      }

      for (const shortcut of shortcutsRef.current) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase()
        
        // Check modifier keys
        const modifiers = shortcut.modifiers || {}
        const modifiersMatch =
          (modifiers.ctrl === undefined || modifiers.ctrl === event.ctrlKey) &&
          (modifiers.shift === undefined || modifiers.shift === event.shiftKey) &&
          (modifiers.alt === undefined || modifiers.alt === event.altKey) &&
          (modifiers.meta === undefined || modifiers.meta === event.metaKey)

        if (keyMatches && modifiersMatch) {
          if (preventDefault) {
            event.preventDefault()
          }
          if (stopPropagation) {
            event.stopPropagation()
          }
          shortcut.action()
          return
        }
      }
    },
    [enabled, preventDefault, stopPropagation]
  )

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, handleKeyDown])
}

/**
 * Hook for list navigation with j/k keys.
 * 
 * WHY: This is a specialized version of useKeyboardShortcuts for
 * navigating lists, the most common use case in the Vetting interface.
 * 
 * @example
 * const { selectedIndex, setSelectedIndex, handlers } = useListNavigation({
 *   items: observations,
 *   onSelect: (obs) => setExpandedId(obs.observation_id),
 * })
 */
export function useListNavigation<T>({
  items,
  onSelect,
  enabled = true,
}: {
  items: T[]
  onSelect?: (item: T, index: number) => void
  enabled?: boolean
}) {
  const selectedIndexRef = useRef(0)

  const selectNext = useCallback(() => {
    const newIndex = Math.min(selectedIndexRef.current + 1, items.length - 1)
    selectedIndexRef.current = newIndex
    if (items[newIndex]) {
      onSelect?.(items[newIndex], newIndex)
    }
  }, [items, onSelect])

  const selectPrev = useCallback(() => {
    const newIndex = Math.max(selectedIndexRef.current - 1, 0)
    selectedIndexRef.current = newIndex
    if (items[newIndex]) {
      onSelect?.(items[newIndex], newIndex)
    }
  }, [items, onSelect])

  const selectFirst = useCallback(() => {
    selectedIndexRef.current = 0
    if (items[0]) {
      onSelect?.(items[0], 0)
    }
  }, [items, onSelect])

  const selectLast = useCallback(() => {
    const lastIndex = items.length - 1
    selectedIndexRef.current = lastIndex
    if (items[lastIndex]) {
      onSelect?.(items[lastIndex], lastIndex)
    }
  }, [items, onSelect])

  useKeyboardShortcuts(
    [
      { key: 'j', action: selectNext, description: 'Next item' },
      { key: 'k', action: selectPrev, description: 'Previous item' },
      { key: 'g', action: selectFirst, description: 'First item' },
      { key: 'G', action: selectLast, modifiers: { shift: true }, description: 'Last item' },
    ],
    { enabled }
  )

  return {
    selectedIndex: selectedIndexRef.current,
    setSelectedIndex: (index: number) => {
      selectedIndexRef.current = index
    },
    selectNext,
    selectPrev,
    selectFirst,
    selectLast,
  }
}

/**
 * Vetting-specific keyboard shortcuts.
 * 
 * WHY: The vetting workflow has specific actions that need quick access.
 * These shortcuts match the first letter of each action for easy memorization.
 */
export interface VettingShortcutActions {
  onConfirm: () => void
  onFalsePositive: () => void
  onMitigated: () => void
  onDeferred: () => void
  onExpand: () => void
  onNext: () => void
  onPrev: () => void
}

export function useVettingShortcuts(
  actions: VettingShortcutActions,
  options: UseKeyboardShortcutsOptions = {}
) {
  useKeyboardShortcuts(
    [
      { key: 'j', action: actions.onNext, description: 'Next observation' },
      { key: 'k', action: actions.onPrev, description: 'Previous observation' },
      { key: 'Enter', action: actions.onExpand, description: 'Expand/collapse' },
      { key: 'v', action: actions.onConfirm, description: 'Confirm as finding' },
      { key: 'f', action: actions.onFalsePositive, description: 'Mark as false positive' },
      { key: 'm', action: actions.onMitigated, description: 'Mark as mitigated' },
      { key: 'd', action: actions.onDeferred, description: 'Defer for review' },
    ],
    options
  )
}

/**
 * Help text for displaying keyboard shortcuts to users.
 */
export const VETTING_SHORTCUTS_HELP = [
  { key: 'j', description: 'Next observation' },
  { key: 'k', description: 'Previous observation' },
  { key: 'Enter', description: 'Expand/collapse details' },
  { key: 'v', description: 'Confirm as vulnerability' },
  { key: 'f', description: 'Mark as false positive' },
  { key: 'm', description: 'Mark as mitigated' },
  { key: 'd', description: 'Defer for later review' },
] as const
